import { useState } from "react";
import { UserMinus } from "lucide-react";
import { Badge, Button } from "@onemileauto/ui";
import type { PermissionLevel } from "@onemileauto/shared-types";
import { useGrantPermission, usePermissions, useRevokePermission } from "../../hooks/use-admin";
import { PERMISSION_LEVEL_OPTIONS, permissionLevelLabel } from "../../lib/permission-labels";

interface MailboxAclPanelProps {
  mailboxId: string;
  mailboxAddress: string;
}

/**
 * Who has access to one mailbox, and a form to grant more (Phase 5: staff
 * mailbox delegation). This is the whole "invite a coworker" flow for this
 * app — entering an email here creates a D1 `users` row for it if one
 * doesn't already exist (see lib/d1.ts#upsertUserByEmail); there's no
 * separate signup step. A non-technical owner just needs to know their
 * staff member's email address.
 */
export function MailboxAclPanel({ mailboxId, mailboxAddress }: MailboxAclPanelProps) {
  const { data, isLoading } = usePermissions(mailboxId);
  const grant = useGrantPermission(mailboxId);
  const revoke = useRevokePermission(mailboxId);

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [level, setLevel] = useState<PermissionLevel>("full");

  const permissions = data?.permissions ?? [];
  const ownerCount = permissions.filter((p) => p.permissionLevel === "owner").length;

  function handleGrant() {
    if (!email.trim()) return;
    grant.mutate(
      { email: email.trim(), displayName: displayName.trim() || undefined, permissionLevel: level },
      { onSuccess: () => { setEmail(""); setDisplayName(""); setLevel("full"); } },
    );
  }

  return (
    <div className="border-t border-border px-4 py-4">
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Who has access to {mailboxAddress}</p>

      {isLoading ? (
        <p className="text-sm text-muted">Loading&hellip;</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {permissions.map((p) => {
            const isSoleOwner = p.permissionLevel === "owner" && ownerCount <= 1;
            return (
              <li
                key={p.userId}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-cream px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-charcoal">{p.displayName}</p>
                  <p className="truncate text-xs text-muted">{p.email}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={p.permissionLevel === "owner" ? "default" : "outline"}>
                    {permissionLevelLabel(p.permissionLevel)}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${p.displayName}'s access`}
                    disabled={isSoleOwner || revoke.isPending}
                    title={isSoleOwner ? "A mailbox needs at least one owner" : undefined}
                    onClick={() => revoke.mutate(p.userId)}
                    className="h-9 w-9 p-0 text-muted hover:text-danger"
                  >
                    <UserMinus size={16} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 flex flex-col gap-2 rounded-md border border-dashed border-border-strong p-3">
        <p className="text-xs font-medium text-muted">Grant access to someone new</p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Their email address"
          className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust"
        />
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Their name (optional)"
          className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust"
        />
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value as PermissionLevel)}
          className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal focus:outline-none focus:ring-2 focus:ring-rust"
        >
          {PERMISSION_LEVEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label} — {o.description}
            </option>
          ))}
        </select>
        <Button type="button" onClick={handleGrant} disabled={!email.trim() || grant.isPending}>
          {grant.isPending ? "Granting access…" : "Grant access"}
        </Button>
        {grant.isError && <p className="text-sm text-danger">Couldn&rsquo;t grant access. Check the email address and try again.</p>}
        {revoke.isError && <p className="text-sm text-danger">Couldn&rsquo;t remove that access. Please try again.</p>}
      </div>
    </div>
  );
}
